const CartDrawer = (() => {
  let isUpdating = false
  const drawer = () => document.getElementById('cart-drawer')
  const overlay = () => document.getElementById('cart-drawer-overlay')
  const guaranteeModal = () => document.getElementById('cart-drawer-guarantee-modal')
  const guaranteeModalOverlay = () => document.getElementById('cart-drawer-guarantee-modal-overlay')

  /* ---- Open / close ---- */
  function open () {
    drawer()?.classList.add('is-open')
    overlay()?.classList.add('is-open')
    document.body.style.overflow = 'hidden'
  }

  function close () {
    closeGuaranteeModal()
    drawer()?.classList.remove('is-open')
    overlay()?.classList.remove('is-open')
    document.body.style.overflow = ''
  }

  /* ---- Guarantee modal open / close ----
     Overlays the cart drawer only — the drawer itself stays mounted
     and open behind it, so its own scrim/state is untouched. ---- */
  function openGuaranteeModal () {
    guaranteeModal()?.classList.add('is-open')
    guaranteeModal()?.setAttribute('aria-hidden', 'false')
    guaranteeModalOverlay()?.classList.add('is-open')
    drawer()?.classList.add('cart-drawer--guarantee-modal-open')
  }

  function closeGuaranteeModal () {
    guaranteeModal()?.classList.remove('is-open')
    guaranteeModal()?.setAttribute('aria-hidden', 'true')
    guaranteeModalOverlay()?.classList.remove('is-open')
    drawer()?.classList.remove('cart-drawer--guarantee-modal-open')
  }

  function isGuaranteeModalOpen () {
    return !!guaranteeModal()?.classList.contains('is-open')
  }

  /* ---- Loading state ---- */
  function setLoading (on) {
    drawer()?.classList.toggle('cart-drawer--loading', on)
  }

  async function refreshCart () {
    try {
      const currentDrawer = drawer()
      const sectionId = currentDrawer?.dataset.sectionId

      if (!sectionId) {
        console.error('[CartDrawer] Missing data-section-id on #cart-drawer.')
        return
      }

      const res = await fetch(`/?sections=${sectionId}`)
      const data = await res.json()

      if (!data[sectionId]) {
        console.error('[CartDrawer] Section not found in response. Check section ID.')
        return
      }

      const html = new DOMParser().parseFromString(data[sectionId], 'text/html')
      const newDrawer = html.getElementById('cart-drawer')

      if (!newDrawer || !currentDrawer) {
        console.error('[CartDrawer] #cart-drawer not found in rendered HTML.')
        return
      }

      // Update cart-derived UI only — keep the review card from initial render.
      const newShippingBar = newDrawer.querySelector('#cart-shipping-bar')
      const oldShippingBar = currentDrawer.querySelector('#cart-shipping-bar')
      if (newShippingBar && oldShippingBar) {
        oldShippingBar.innerHTML = newShippingBar.innerHTML
      }

      const newBody = newDrawer.querySelector('#cart-drawer-body')
      const oldBody = currentDrawer.querySelector('#cart-drawer-body')
      if (newBody && oldBody) {
        oldBody.innerHTML = newBody.innerHTML
      }

      const newFooter = newDrawer.querySelector('#cart-drawer-footer')
      const oldFooter = currentDrawer.querySelector('#cart-drawer-footer')
      if (oldFooter) {
        oldFooter.style.display = newFooter ? newFooter.style.display : 'none'

        if (newFooter && oldFooter.style.display !== 'none') {
          const newFooterContainer = newFooter.querySelector('.cart-drawer__footer-container')
          const oldFooterContainer = oldFooter.querySelector('.cart-drawer__footer-container')
          if (newFooterContainer && oldFooterContainer) {
            oldFooterContainer.innerHTML = newFooterContainer.innerHTML
          }
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated'))

      if (document.querySelector('[js-cart-upsell-slider]')) {
        const flickity = new Flickity(document.querySelector('[js-cart-upsell-slider]'), {
          prevNextButtons: false,
          adaptiveHeight: false,
          wrapAround: false,
          pageDots: false,
          contain: true,
          imagesLoaded: true,
          cellAlign: 'left',
        })
        flickity.resize()
      }
    } catch (err) {
      console.error('[CartDrawer] refreshCart error:', err)
    }
  }

  /* ---- Update cart count badge in header ---- */
  async function refreshCount () {
    try {
      const res = await fetch('/cart.js')
      const data = await res.json()
      document.querySelectorAll('cart-count').forEach(el => {
        el.textContent = data.item_count
        data.item_count > 0 ? el.classList.remove("opacity-0") : el.classList.add("opacity-0")
      });
    } catch (_) {}
  }

  /* ---- Update quantity ---- */
  async function updateQty (key, qty) {
    if (isUpdating) return
    qty = parseInt(qty, 10)
    if (isNaN(qty) || qty < 0) return

    isUpdating = true
    setLoading(true)

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })

      if (!res.ok) throw new Error('Cart change failed')

      await refreshCart()
      await refreshCount()
    } catch (err) {
      console.error('[CartDrawer] updateQty error:', err)
    } finally {
      isUpdating = false
      setLoading(false)
    }
  }

  /* ---- Remove item ----
     A single line item can render as TWO <li> elements (paid row + free row),
     both sharing the same data-key. Animate ALL of them before updating qty.
  ---- */
  async function removeItem (key) {
    const rows = document.querySelectorAll(`.cart-item[data-key="${key}"]`)
    rows.forEach(row => {
      row.style.transition = 'opacity 0.25s, transform 0.25s'
      row.style.opacity    = '0'
      row.style.transform  = 'translateX(30px)'
    })
    await new Promise(r => setTimeout(r, 250))
    await updateQty(key, 0)
  }

  /* ---- Apply discount code ---- */
  async function applyDiscount() {
    const input = document.getElementById('cart-discount-input');
    const code  = input?.value?.trim();
    if (!code) return;

    const btn = document.querySelector('.cart-drawer__discount-btn');

    // Show loading state
    if (btn) {
      btn.disabled    = true;
      btn.textContent = '...';
    }

    try {
      // Step 1 — Set discount session cookie
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });

      // Step 2 — Append to checkout URL as fallback
      const checkoutBtn = document.getElementById('cart-checkout-btn');
      if (checkoutBtn) {
        checkoutBtn.setAttribute('href', `/checkout?discount=${encodeURIComponent(code)}`);
      }

      // Step 3 — Refresh cart to show updated prices + discount tag
      await refreshCart();

      // Step 4 — Clear input
      if (input) input.value = '';

      // Step 5 — Show success feedback
      if (btn) {
        btn.textContent      = '✓ Gespeichert';
        btn.style.background = 'var(--cart-accent)';
        btn.style.color      = '#fff';
        setTimeout(() => {
          btn.textContent      = 'Anwenden';
          btn.style.background = '';
          btn.style.color      = '';
          btn.disabled         = false;
        }, 2000);
      }

    } catch (err) {
      console.error('[CartDrawer] applyDiscount error:', err);

      if (btn) {
        btn.textContent      = '✗ Ungültig';
        btn.style.background = '#e44';
        btn.style.color      = '#fff';
        setTimeout(() => {
          btn.textContent      = 'Anwenden';
          btn.style.background = '';
          btn.style.color      = '';
          btn.disabled         = false;
        }, 2000);
      }
    }
  }

  // ✅ Remove discount by redirecting to /discount/REMOVE then refreshing
  async function removeDiscount() {
    try {
      // ✅ Correct way — update cart attributes to clear discount
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });

      // Reset checkout button href
      const checkoutBtn = document.getElementById('cart-checkout-btn');
      if (checkoutBtn) {
        checkoutBtn.setAttribute('href', '/checkout');
      }

      await refreshCart();

    } catch (err) {
      console.error('[CartDrawer] removeDiscount error:', err);
    }
  }

  /* ---- Add upsell product to cart ---- */
  async function addUpsell(btn) {
    const variantId = btn?.dataset.variantId;
    if (!variantId) return;

    const originalText = btn.textContent;
    btn.disabled    = true;
    btn.textContent = '...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!res.ok) throw new Error('Upsell add failed');

      // ✅ Refresh cart — upsell card disappears automatically
      // because product is now in cart (already_in_cart = true)
      await refreshCart();
      await refreshCount();

    } catch (err) {
      console.error('[CartDrawer] addUpsell error:', err);
      btn.disabled    = false;
      btn.textContent = originalText;
    }
  }

  /* ---- Delegated event listeners ----
     +/- buttons now read data-qty from the <li> instead of input.value.

     Why: when a line item is split into a paid row + free row, each row's
     qty input shows only its portion (e.g. 2 and 1), not the total (3).
     data-qty always holds the true total, so +1 / -1 is always correct.
  ---- */
  document.addEventListener('click', async function (e) {

    // Plus button
    if (e.target.closest('.qty-btn--plus')) {
      e.preventDefault()
      e.stopPropagation()
      const item     = e.target.closest('.cart-item')
      const key      = item?.dataset.key
      const totalQty = parseInt(item?.dataset.qty || 0)
      if (key) await updateQty(key, totalQty + 1)
    }

    // Minus button
    if (e.target.closest('.qty-btn--minus')) {
      e.preventDefault()
      e.stopPropagation()
      const item     = e.target.closest('.cart-item')
      const key      = item?.dataset.key
      const totalQty = parseInt(item?.dataset.qty || 0)
      if (key && totalQty > 0) await updateQty(key, totalQty - 1)
    }

    // Remove button
    if (e.target.closest('.cart-item__remove')) {
      e.preventDefault()
      e.stopPropagation()
      const key = e.target.closest('.cart-item')?.dataset.key
      if (key) await removeItem(key)
    }

    // Cart icon
    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault()
      open()
    }
  })

  /* ---- Keyboard close ----
     Esc closes the guarantee modal first when it's open, then the drawer. ---- */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return
    if (isGuaranteeModalOpen()) {
      closeGuaranteeModal()
    } else {
      close()
    }
  })

  return {
    open,
    close,
    openGuaranteeModal,
    closeGuaranteeModal,
    updateQty,
    removeItem,
    applyDiscount,
    removeDiscount,
    refreshCart,
    addUpsell,
    refreshCount,
  }
})()
